import { useCallback, useEffect, useSyncExternalStore } from 'react'

const PREFIX = 'vp-member-catalog-unlock:'
const inflight = new Map<string, Set<string>>()
let unlockVersion = 0
const unlockListeners = new Set<() => void>()

function emitUnlocks() {
  unlockVersion += 1
  for (const listener of unlockListeners) listener()
}

function subscribeUnlocks(listener: () => void) {
  unlockListeners.add(listener)
  return () => {
    unlockListeners.delete(listener)
  }
}

export type CatalogUnlockItem = {
  productId: string
  accessStatus: string
}

type Snapshot = {
  locked: string[]
  celebrated: string[]
}

export function catalogUnlockStorageKey(producerId: string, email: string) {
  return `${PREFIX}${producerId}:${email.trim().toLowerCase()}`
}

function emptySnapshot(): Snapshot {
  return { locked: [], celebrated: [] }
}

function readSnapshot(storage: Pick<Storage, 'getItem'>, key: string): Snapshot {
  try {
    const parsed = JSON.parse(storage.getItem(key) ?? 'null') as Snapshot | null
    if (!parsed || !Array.isArray(parsed.locked) || !Array.isArray(parsed.celebrated)) return emptySnapshot()
    return { locked: parsed.locked, celebrated: parsed.celebrated }
  } catch {
    return emptySnapshot()
  }
}

function writeSnapshot(storage: Pick<Storage, 'getItem' | 'setItem'>, key: string, snapshot: Snapshot) {
  storage.setItem(key, JSON.stringify(snapshot))
}

export function syncCatalogUnlocks(
  storageKey: string,
  items: readonly CatalogUnlockItem[],
  storage: Pick<Storage, 'getItem' | 'setItem'> = window.localStorage,
): string[] {
  const lockedNow = items.filter((item) => item.accessStatus === 'LOCKED').map((item) => item.productId)
  const ownedNow = items.filter((item) => item.accessStatus !== 'LOCKED').map((item) => item.productId)
  const previous = readSnapshot(storage, storageKey)
  const discovered = ownedNow.filter(
    (productId) => previous.locked.includes(productId) && !previous.celebrated.includes(productId),
  )
  writeSnapshot(storage, storageKey, {
    locked: lockedNow,
    celebrated: [...new Set([...previous.celebrated, ...discovered])],
  })
  let pending = inflight.get(storageKey)
  if (!pending) {
    pending = new Set()
    inflight.set(storageKey, pending)
  }
  for (const productId of discovered) pending.add(productId)
  emitUnlocks()
  return [...pending]
}

export function finishCatalogUnlock(storageKey: string, productId: string) {
  inflight.get(storageKey)?.delete(productId)
  emitUnlocks()
}

export function resetCatalogUnlockMemory() {
  inflight.clear()
  emitUnlocks()
}

export function useCatalogUnlocks(storageKey: string | null, items: readonly CatalogUnlockItem[] | null) {
  const version = useSyncExternalStore(subscribeUnlocks, () => unlockVersion, () => 0)
  const fingerprint = items?.map((item) => `${item.productId}:${item.accessStatus}`).join('|') ?? ''

  useEffect(() => {
    if (!storageKey || !items) return
    syncCatalogUnlocks(storageKey, items)
    // fingerprint já resume o conteúdo de items; a identidade do array não dispara nova gravação.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, storageKey])

  const unlockingIds =
    version >= 0 && storageKey ? [...(inflight.get(storageKey) ?? [])].sort() : []

  const dismissUnlock = useCallback(
    (productId: string) => {
      if (storageKey) finishCatalogUnlock(storageKey, productId)
    },
    [storageKey],
  )

  return { unlockingIds, dismissUnlock }
}
